import React, { useEffect, useState } from 'react';
import { FormControl, Grid } from '@mui/material';
import { TextField } from '@biq/apex-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { EDGE_DEFINITION_PATHS } from '../../../../constants/edge-definition-paths';
import { useAuth, usePermission } from '../../../../core';
import { useDictionaryList, useMutateEdgeDefinition } from '../../../../hooks';
import {
    Dictionary,
    EdgeDefinition,
    possiblyNavigateToMetadataSearch,
    validationsMessages,
} from '../../../../lib';
import {
    AttributeAssociationItem,
    defaultAttributeAssociationItem,
    defaultAttributeAssociationList,
    defaultEdgeDefinition,
    EDGE_CONDITION_ERRORS,
    getSortedDictionaryAttributesFor,
} from '../../edge-definition-helper';
import { AttributeAssociationWidget } from './AttributeAssociationWidget';
import {
    AutocompleteChainSelector,
    CreateFormFeatureDialog,
    FormLabel,
} from 'components';
import {
    createEdgeDefinitionStyles,
    edgeDefinitionConfig,
    edgeSchema,
} from './create-edge-definition-helper';

export const CreateEdgeDefinition = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { hasPermissions } = usePermission();

    const [dictionaries, setDictionaries] = useState<Dictionary[]>([]);
    const [fromDictionaryAttributes, setFromDictionaryAttributes] = useState<
        string[]
    >([]);
    const [toDictionaryAttributes, setToDictionaryAttributes] = useState<
        string[]
    >([]);
    const [attributeAssociationList, setAttributeAssociationList] = useState<
        AttributeAssociationItem[]
    >(defaultAttributeAssociationList);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);

    const {
        formState: { errors },
        watch,
        control,
        setError,
        setValue,
        clearErrors,
    } = useForm<EdgeDefinition>({
        mode: 'all',
        defaultValues: defaultEdgeDefinition,
        resolver: yupResolver(edgeSchema),
    });

    const fromDic = watch('fromDictionary');
    const toDic = watch('toDictionary');
    const dictionaryName = watch('name');

    const { mutate: saveEdgeDefinition } = useMutateEdgeDefinition();
    const {
        data: dictionaryData,
        isFetched: isDictionaryDataFetched,
        isSuccess: isDictionaryDataSuccess,
    } = useDictionaryList();

    const updateFromDictionaryAttributes = (): void => {
        const fromDictionaryAttributeList = getSortedDictionaryAttributesFor(
            fromDic,
            dictionaries
        );
        setFromDictionaryAttributes(fromDictionaryAttributeList);
    };

    const updateToDictionaryAttributes = (): void => {
        const toDictionaryAttributeList = getSortedDictionaryAttributesFor(
            toDic,
            dictionaries
        );
        setToDictionaryAttributes(toDictionaryAttributeList);
    };

    const resetAttributeAssociationList = (): void => {
        setAttributeAssociationList([
            {
                ...defaultAttributeAssociationItem,
                id: crypto.randomUUID(),
            },
        ]);
    };

    useEffect(() => {
        if (
            dictionaryData &&
            isDictionaryDataFetched &&
            isDictionaryDataFetched
        ) {
            setDictionaries(dictionaryData?.data ?? []);
        }
    }, [dictionaryData, isDictionaryDataSuccess, isDictionaryDataFetched]);

    useEffect(() => {
        updateFromDictionaryAttributes();
        resetAttributeAssociationList();
    }, [fromDic]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        updateToDictionaryAttributes();
        resetAttributeAssociationList();
    }, [toDic]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleEdgeDefinitionNavigation = (): void => {
        possiblyNavigateToMetadataSearch(
            navigate,
            EDGE_DEFINITION_PATHS.LANDING_PAGE,
            searchParams
        );
    };

    const handleItemsChange = (list: AttributeAssociationItem[]): void => {
        setAttributeAssociationList(list);
    };

    const possiblySaveEdgeDefinitionForm = (): void => {
        const permission = hasPermissions('createMetadata');
        if (permission) {
            const newEdgeDefinition: EdgeDefinition = {
                name: dictionaryName,
                fromDictionary: fromDic,
                toDictionary: toDic,
                condition: attributeAssociationList.map((association) => {
                    return {
                        fromCol: association.attributeAssociation.leftSideValue,
                        toCol: association.attributeAssociation.rightSideValue,
                    };
                }),
            };

            saveEdgeDefinition(
                { edgeDefinition: newEdgeDefinition, token: getToken() },
                {
                    onSuccess: () => {
                        setTimeout(() => {
                            handleEdgeDefinitionNavigation();
                        }, 500);
                    },
                }
            );
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const isFormValid = (): boolean => {
        const isNameSelected = !!dictionaryName;
        const isFromDictionarySelected = !!fromDic;
        const isToDictionarySelected = !!toDic;
        let areAssociationsValid = true;
        const updatedList = attributeAssociationList.map((association) => {
            const isEmptyLeftSideValue =
                !association.attributeAssociation.leftSideValue;
            const isEmptyRightSideValue =
                !association.attributeAssociation.rightSideValue;

            // if at least one of the values is empty, the whole list is considered as not valid.
            if (isEmptyLeftSideValue || isEmptyRightSideValue)
                areAssociationsValid = false;

            return {
                ...association,
                leftSideError: isEmptyLeftSideValue,
                leftSideErrorMessage: isEmptyLeftSideValue
                    ? EDGE_CONDITION_ERRORS.EMPTY_CONDITION
                    : '',
                rightSideError: isEmptyRightSideValue,
                rightSideErrorMessage: isEmptyRightSideValue
                    ? EDGE_CONDITION_ERRORS.EMPTY_CONDITION
                    : '',
            };
        });

        if (!isNameSelected) setError('name', { type: 'required' });
        if (!isFromDictionarySelected)
            setError('fromDictionary', { type: 'required' });
        if (!isToDictionarySelected)
            setError('toDictionary', { type: 'required' });
        setAttributeAssociationList(updatedList);

        return (
            isNameSelected &&
            isFromDictionarySelected &&
            isToDictionarySelected &&
            areAssociationsValid
        );
    };

    const onSubmit = (): void => {
        if (isFormValid()) {
            possiblySaveEdgeDefinitionForm();
        }
    };

    const handleDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const onChangeTextField = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const edgeName: string = event.currentTarget.value;
        if (edgeName) {
            clearErrors('name');
        } else {
            setError('name', { type: 'required' });
        }
        setValue('name', edgeName);
    };

    return (
        <CreateFormFeatureDialog
            buttonText='Save'
            handleSubmit={onSubmit}
            handleCloseButton={handleEdgeDefinitionNavigation}
            handleBackButton={handleEdgeDefinitionNavigation}
            listConfig={edgeDefinitionConfig}
        >
            <Grid sx={createEdgeDefinitionStyles.mainContainer}>
                <Grid sx={createEdgeDefinitionStyles.textFieldContainer}>
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Name'
                                required
                                onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                ): void => onChangeTextField(event)}
                                sx={createEdgeDefinitionStyles.nameTextField}
                                error={!!errors.name}
                                errorText={
                                    errors.name?.type === 'required'
                                        ? validationsMessages.required
                                        : errors.name?.message
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid sx={createEdgeDefinitionStyles.dictionariesContainer}>
                    <FormControl
                        sx={createEdgeDefinitionStyles.fromDictionaryTextField}
                    >
                        <Grid
                            sx={createEdgeDefinitionStyles.formLabelContainer}
                        >
                            <FormLabel
                                required
                                text='From Dictionary'
                                condensed
                            />
                        </Grid>
                        <Grid>
                            <AutocompleteChainSelector
                                tooltipTitle={fromDic}
                                controlName='fromDictionary'
                                selectedChain={fromDic}
                                control={control}
                                error={!!errors.fromDictionary}
                                errorText={
                                    errors.fromDictionary
                                        ? validationsMessages.required
                                        : ''
                                }
                                autocompleteStyles={
                                    createEdgeDefinitionStyles.dictionaryAutocomplete
                                }
                            />
                        </Grid>
                    </FormControl>
                    <FormControl
                        sx={createEdgeDefinitionStyles.dictionaryTextField}
                    >
                        <Grid
                            sx={createEdgeDefinitionStyles.formLabelContainer}
                        >
                            <FormLabel
                                required
                                text='To Dictionary'
                                condensed
                            />
                        </Grid>
                        <Grid>
                            <AutocompleteChainSelector
                                tooltipTitle={toDic}
                                controlName='toDictionary'
                                selectedChain={toDic}
                                control={control}
                                error={!!errors.toDictionary}
                                errorText={
                                    errors.toDictionary
                                        ? validationsMessages.required
                                        : ''
                                }
                                autocompleteStyles={
                                    createEdgeDefinitionStyles.dictionaryAutocomplete
                                }
                            />
                        </Grid>
                    </FormControl>
                </Grid>
                <Grid
                    sx={
                        createEdgeDefinitionStyles.attributeAssociationContainer
                    }
                >
                    <AttributeAssociationWidget
                        handleAttributeAssociationsChanged={handleItemsChange}
                        attributeAssociationList={attributeAssociationList}
                        leftSideSelectionOptions={fromDictionaryAttributes}
                        rightSideSelectionOptions={toDictionaryAttributes}
                    />
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handleDialogClose}
                />
            )}
        </CreateFormFeatureDialog>
    );
};
